import { useCallback, useContext, useMemo } from 'react';
import { createId } from '@paralleldrive/cuid2';
import type { Chat, InputAttachment } from '@trinity/chat';
import { ChatFileUploadStatus, ChatQueryStatus } from '@trinity/chat';
import { SidePanelExpandedContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { transformRemirrorState } from '@trinity/chat-components/components/Remirror/utils/transform-remirror-state';
import { useActiveApplicationIdentifier } from '@trinity/chat-components/containers/UserApplications/useActiveApplicationIdentifier';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useChatApiClient } from '../../containers/Chat/useApiClient.js';
import {
  ActivePageContext,
  SubmitMessageContext,
  ChatSessionInputContext,
  ChatSessionFileUploadsContext,
  IsPreparingUploadContext,
  ChatHasAccessContext,
  ChatSessionQueriesContext,
  ChatSessionPendingHintPromptIdentifierContext,
  EditorDocContext,
  ChatSessionPendingAttachmentsContext,
} from './context';
import { useSelectedQueryParentIdentifier } from './hooks/useSelectedQueryParentIdentifier';
import { useSubmitQueryMutation } from './hooks/useSubmitQueryMutation';
import { useUploadInputAttachment } from './hooks/useUploadInputAttachment';
import type { Context } from './interfaces.context';

type Props = {
  children: React.ReactNode;
};

/**
 * Returns the identifier of the first in-progress query, or null if none.
 */
const getInProgressQueryIdentifier = (queries: Record<string, Chat.Query>): string | null => {
  if (!queries) return null;

  const found = Object.values(queries).find(
    (query) =>
      query.status === ChatQueryStatus.Loading ||
      query.status === ChatQueryStatus.LoadingExtended ||
      query.status === ChatQueryStatus.Streaming
  );

  return found?.identifier ?? null;
};

export const SubmitMessageContainer = (props: Props) => {
  const input = useContext(ChatSessionInputContext);
  const fileUploads = useContext(ChatSessionFileUploadsContext);
  const queries = useContext(ChatSessionQueriesContext);
  const pendingHintPromptIdentifier = useContext(ChatSessionPendingHintPromptIdentifierContext);
  const editorDocRef = useContext(EditorDocContext);
  const getSelectedQueryParentIdentifier = useSelectedQueryParentIdentifier();
  const hasAccess = useContext(ChatHasAccessContext);
  const pendingAttachments = useContext(ChatSessionPendingAttachmentsContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifierNullSafe();
  const applicationIdentifier = useActiveApplicationIdentifier();

  const { isPending, mutateAsync } = useSubmitQueryMutation();
  const isPreparingUpload = useContext(IsPreparingUploadContext);
  const { clearPage } = useContext(ActivePageContext);
  const [isExpanded] = useContext(SidePanelExpandedContext);
  const uploadInputAttachment = useUploadInputAttachment();
  const apiClient = useChatApiClient();

  const handleSubmit = useCallback(async () => {
    if (!isExpanded) {
      clearPage();
    }

    const identifier = createId();
    const parentQueryIdentifier = getSelectedQueryParentIdentifier();

    const editorDoc = editorDocRef.current;
    const { text: queryText, mentions } = editorDoc
      ? transformRemirrorState(editorDoc)
      : { text: input, mentions: [] };

    const stagedAttachments = pendingAttachments.filter((a) => a.status === 'staged');

    // Include already-uploaded attachments so they aren't dropped on submit.
    const existingAttachments: InputAttachment[] = pendingAttachments
      .filter((a): a is typeof a & { fileUri: string } => a.status === 'success' && !!a.fileUri)
      .map((a) => ({
        type: 'file' as const,
        identifier: a.identifier,
        fileUri: a.fileUri,
        name: a.name,
        mimeType: a.mimeType as InputAttachment['mimeType'],
        sizeBytes: a.sizeBytes,
      }));

    let inputAttachments: InputAttachment[] | undefined =
      existingAttachments.length > 0 ? existingAttachments : undefined;

    if (stagedAttachments.length > 0) {
      try {
        const { uploads } = await apiClient.chat.getInputAttachmentUploadCredentials({
          brandIdentifier: brandIdentifier ?? undefined,
          applicationIdentifier: applicationIdentifier ?? undefined,
          files: stagedAttachments.map((a) => ({
            name: a.name,
            mimeType: a.mimeType,
            sizeBytes: a.sizeBytes,
          })),
        });

        for (const [i, upload] of uploads.entries()) {
          dispatch({
            type: 'pending-attachment/upload-started',
            payload: { clientId: stagedAttachments[i].clientId, identifier: upload.identifier },
          });
        }

        const results = await Promise.allSettled(
          uploads.map((upload, i) => uploadInputAttachment(stagedAttachments[i].file, upload))
        );

        if (results.some((r) => r.status === 'rejected')) return;

        const newlyUploaded: InputAttachment[] = uploads.map((upload, i) => ({
          type: 'file' as const,
          identifier: upload.identifier,
          fileUri: `s3://${upload.bucket}/${upload.s3Key}`,
          name: stagedAttachments[i].name,
          mimeType: stagedAttachments[i].mimeType as InputAttachment['mimeType'],
          sizeBytes: stagedAttachments[i].sizeBytes,
        }));

        // Append newly-uploaded to any already-successful attachments.
        inputAttachments = [...(inputAttachments ?? []), ...newlyUploaded];
      } catch {
        for (const a of stagedAttachments) {
          dispatch({
            type: 'pending-attachment/upload-failed',
            payload: { clientId: a.clientId, errorMessage: 'Failed to get upload credentials' },
          });
        }
        return;
      }
    }

    return mutateAsync({
      action: null,
      identifier,
      parentQueryIdentifier,
      query: queryText,
      promptIdentifier: pendingHintPromptIdentifier ?? undefined,
      mentions: mentions.length > 0 ? mentions : undefined,
      inputAttachments,
    });
  }, [
    clearPage,
    editorDocRef,
    getSelectedQueryParentIdentifier,
    input,
    mutateAsync,
    isExpanded,
    pendingHintPromptIdentifier,
    pendingAttachments,
    dispatch,
    uploadInputAttachment,
    apiClient,
    brandIdentifier,
  ]);

  const submitState = useMemo((): Context.SubmitMessageMetadata => {
    const inProgressIdentifier = getInProgressQueryIdentifier(queries);

    if (isPending || inProgressIdentifier !== null) {
      return { status: 'submitting-query', queryIdentifier: inProgressIdentifier };
    }

    if (
      isPreparingUpload ||
      fileUploads.some((s) => s.items.some((i) => i.status !== ChatFileUploadStatus.Ready)) ||
      pendingAttachments.some((a) => a.status === 'uploading')
    ) {
      return { status: 'uploading-file' };
    }

    const hasSubmittableAttachments = pendingAttachments.some(
      (a) => a.status === 'staged' || a.status === 'success'
    );
    if ((input.trim().length === 0 && !hasSubmittableAttachments) || !hasAccess) {
      return { status: 'disabled' };
    }

    return { status: 'inactive' };
  }, [fileUploads, hasAccess, input, isPending, isPreparingUpload, pendingAttachments, queries]);

  const submitCtx = useMemo(
    () => ({
      state: submitState,
      onSubmit: handleSubmit,
    }),
    [submitState, handleSubmit]
  );

  return <SubmitMessageContext.Provider value={submitCtx}>{props.children}</SubmitMessageContext.Provider>;
};
